import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';

import { useOnboardingContext } from '../../../../Contexts/OnboardingContext';

const SignUpFlowContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const SIGN_UP_STEPS_REFRESH_STARTED = 'sign-up-steps-refresh-started';
export const SIGN_UP_STEPS_REFRESH_SUCCEEDED = 'sign-up-steps-refresh-succeeded';
export const SIGN_UP_STEPS_REFRESH_FAILED = 'sign-up-steps-refresh-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    signUpStepsCollection: null,
    isSignUpStepsRefreshing: false,
    signUpStepsRefreshError: null,
    activeStep: null,
  };
};

const getActiveStep = (coll) => {
  return coll.resources().list().find((resource) => {
    return resource.attr('isActiveStep') === 'true';
  });
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        signUpStepsCollection: action.signUpStepsCollection,
        activeStep: getActiveStep(action.signUpStepsCollection),
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SIGN_UP_STEPS_REFRESH_STARTED: {
      return {
        ...state,
        isSignUpStepsRefreshing: true,
        signUpStepsRefreshError: null,
      };
    }
    case SIGN_UP_STEPS_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isSignUpStepsRefreshing: false,
        signUpStepsRefreshError: null,
        signUpStepsCollection: action.signUpStepsCollection,
        activeStep: getActiveStep(action.signUpStepsCollection),
      };
    }
    case SIGN_UP_STEPS_REFRESH_FAILED: {
      return {
        ...state,
        isSignUpStepsRefreshing: false,
        signUpStepsRefreshError: action.signUpStepsRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const SignUpFlowProvider = ({
  children,
}) => {
  const { householdResource } = useOnboardingContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshSignUpStepsCollection = React.useCallback(async () => {
    try {
      dispatch({ type: SIGN_UP_STEPS_REFRESH_STARTED });
      const coll = await householdResource.relationship(fr.relationships.signUpSteps.name).link('related').get();
      dispatch({ type: SIGN_UP_STEPS_REFRESH_SUCCEEDED, signUpStepsCollection: coll });
    } catch (err) {
      dispatch({ type: SIGN_UP_STEPS_REFRESH_FAILED, signUpStepsRefreshError: err });
    }
  }, [householdResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      plannerResource: state.plannerResource,
      signUpStepsCollection: state.signUpStepsCollection,
      isSignUpStepsRefreshing: state.isSignUpStepsRefreshing,
      signUpStepsRefreshError: state.signUpStepsRefreshError,
      activeStep: state.activeStep,
      refreshSignUpStepsCollection,
    };
  }, [
    state,
    refreshSignUpStepsCollection,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const signUpStepsResp = await householdResource.withRelationship(
        fr.relationships.signUpSteps.name,
        (rel) => rel.link('related').get(),
        () => fr.collection(),
      );

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        signUpStepsCollection: signUpStepsResp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <SignUpFlowContext.Provider value={memoizedValue}>
      {children}
    </SignUpFlowContext.Provider>
  );
};

// Use SignUpFlow State Context
const useSignUpFlowContext = () => {
  const context = React.useContext(SignUpFlowContext);
  if (context === undefined) {
    throw new Error('useSignUpFlowContext must be used within a SignUpFlowProvider');
  }
  return context;
};

SignUpFlowProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SignUpFlowProvider,
  useSignUpFlowContext,
};
